import messaging, { FirebaseMessagingTypes } from '@react-native-firebase/messaging';
import { Platform, PermissionsAndroid } from 'react-native';
import notifee, { AndroidImportance } from '@notifee/react-native';
import { v2RegisterFCMToken, v2UnregisterFCMToken, v2UnregisterAllFCMTokens } from './v2/api';
import { badgeManager } from './badge';

// Notification types for handling different notification payloads
export interface NotificationData {
    type?: string;
    userId?: string;
    badge_count?: string; // Badge count sent from backend (as string in FCM data)
    document_id?: string;
    [key: string]: string | undefined;
}

export interface NotificationPayload {
    title?: string;
    body?: string;
    data?: NotificationData;
}

class NotificationService {
    private isInitialized = false;
    private currentToken: string | null = null;
    /** The one onTokenRefresh subscription while signed in; null when there is none. */
    private tokenRefreshUnsubscribe: (() => void) | null = null;
    private _isChatActive = false;
    private _onNavigate: ((screen: string) => void) | null = null;
    private _onChatMessage: (() => void) | null = null;
    private _onBalanceRefresh: (() => void) | null = null;

    /**
     * Устанавливается из ChatContext для подавления foreground-уведомлений когда чат открыт
     */
    setChatActive(active: boolean): void {
        this._isChatActive = active;
    }

    /**
     * Устанавливается из App для навигации по нажатию на уведомление
     */
    setNavigationHandler(handler: (screen: string) => void): void {
        this._onNavigate = handler;
    }

    /**
     * Устанавливается из ChatContext для мгновенного обновления сообщений при получении пуша
     */
    setOnChatMessageHandler(handler: () => void): void {
        this._onChatMessage = handler;
    }

    /**
     * Устанавливается из DashboardV2Screen для принудительного рефреша Total
     * balance когда приходит push о новой Payoma-транзакции (data.type ===
     * 'payoma_transaction'). Backend к этому моменту уже синхронизировал баланс
     * с Payoma, так что fetchBalances() покажет новую сумму.
     */
    setOnBalanceRefreshHandler(handler: (() => void) | null): void {
        this._onBalanceRefresh = handler;
    }

    /**
     * Initialize the notification service
     * Should be called early in app lifecycle
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) {
            return;
        }

        try {
            // Request permission on iOS
            if (Platform.OS === 'ios') {
                const authStatus = await messaging().requestPermission();
                const enabled =
                    authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
                    authStatus === messaging.AuthorizationStatus.PROVISIONAL;

                if (!enabled) {
                    console.log('Push notification permission not granted');
                    return;
                }
            }

            // Request permission on Android 13+
            if (Platform.OS === 'android' && Platform.Version >= 33) {
                const granted = await PermissionsAndroid.request(
                    PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS
                );
                if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
                    console.log('Push notification permission not granted on Android');
                    return;
                }
            }

            // Set up message handlers
            this.setupMessageHandlers();

            this.isInitialized = true;
            console.log('Notification service initialized');
        } catch (error) {
            console.error('Failed to initialize notification service:', error);
        }
    }

    /**
     * Get the FCM token and register it with the backend
     * Note: FCM/APNs does NOT work on iOS simulators - this is an Apple limitation.
     * Push notifications require a physical iOS device.
     */
    async registerToken(): Promise<string | null> {
        try {
            // Get the FCM token
            const token = await messaging().getToken();

            if (!token) {
                console.log('Failed to get FCM token - this is expected on iOS simulator');
                return null;
            }

            // Register the token with the backend
            const deviceType = Platform.OS as 'ios' | 'android';
            await v2RegisterFCMToken(token, deviceType);

            this.currentToken = token;
            console.log('FCM token registered successfully');

            // Listen for token refresh — once. Every sign-in used to add
            // another listener and none was ever removed, so one refresh
            // registered the token once per sign-in, after sign-out too.
            if (!this.tokenRefreshUnsubscribe) {
                this.tokenRefreshUnsubscribe = messaging().onTokenRefresh(async (newToken) => {
                    console.log('FCM token refreshed');
                    await this.handleTokenRefresh(newToken);
                });
            }

            return token;
        } catch (error) {
            // On iOS simulator, FCM token registration will fail because APNs is not available
            // This is expected behavior - push notifications only work on physical iOS devices
            if (Platform.OS === 'ios') {
                console.warn(
                    'Failed to register FCM token on iOS. ' +
                    'If running on simulator, this is expected - push notifications require a physical device. ' +
                    'Error:',
                    error
                );
            } else {
                console.error('Failed to register FCM token:', error);
            }
            return null;
        }
    }

    /**
     * Handle token refresh
     */
    private async handleTokenRefresh(newToken: string): Promise<void> {
        try {
            const deviceType = Platform.OS as 'ios' | 'android';
            await v2RegisterFCMToken(newToken, deviceType);
            this.currentToken = newToken;
            console.log('Refreshed FCM token registered');
        } catch (error) {
            console.error('Failed to register refreshed FCM token:', error);
        }
    }

    /**
     * Unregister the current token (on logout)
     */
    async unregisterToken(): Promise<void> {
        // Signed out, a refreshed token belongs to nobody: stop listening.
        if (this.tokenRefreshUnsubscribe) {
            this.tokenRefreshUnsubscribe();
            this.tokenRefreshUnsubscribe = null;
        }
        try {
            if (this.currentToken) {
                await v2UnregisterFCMToken(this.currentToken);
                this.currentToken = null;
                console.log('FCM token unregistered');
            }
            // Defence in depth: force FCM to issue a brand-new device token
            // the next time getToken() is called. Without this, if the next
            // user logs in on this device they'd reuse the outgoing user's
            // token, and any server-side row that wasn't cleaned up (network
            // error, app killed before DELETE was sent) would still steer
            // pushes to this device. Failures here are non-fatal — the
            // server-side Upsert reassignment is the real safety net.
            try {
                await messaging().deleteToken();
                console.log('Local FCM token deleted; a fresh one will be issued on next login');
            } catch (deleteErr) {
                console.warn('Failed to delete local FCM token:', deleteErr);
            }
        } catch (error) {
            console.error('Failed to unregister FCM token:', error);
        }
    }

    /**
     * Unregister all tokens for the user (on logout from all devices)
     */
    async unregisterAllTokens(): Promise<void> {
        try {
            await v2UnregisterAllFCMTokens();
            this.currentToken = null;
            console.log('All FCM tokens unregistered');
        } catch (error) {
            console.error('Failed to unregister all FCM tokens:', error);
        }
    }

    /**
     * Set up message handlers for foreground and background messages
     */
    private setupMessageHandlers(): void {
        // Handle foreground messages
        messaging().onMessage(async (remoteMessage) => {
            console.log('Foreground message received:', remoteMessage);
            this.handleForegroundMessage(remoteMessage);
        });

        // Handle background/quit state messages that opened the app
        messaging().onNotificationOpenedApp((remoteMessage) => {
            console.log('Notification opened app:', remoteMessage);
            this.handleNotificationOpened(remoteMessage);
        });

        // Check if app was opened from a notification (quit state)
        messaging()
            .getInitialNotification()
            .then((remoteMessage) => {
                if (remoteMessage) {
                    console.log('App opened from quit state by notification:', remoteMessage);
                    this.handleNotificationOpened(remoteMessage);
                }
            });
    }

    /**
     * Показ системного уведомления через notifee (foreground)
     */
    private async handleForegroundMessage(remoteMessage: FirebaseMessagingTypes.RemoteMessage): Promise<void> {
        const notification = remoteMessage.notification;
        const data = remoteMessage.data as NotificationData | undefined;

        console.log('[PUSH] Foreground message:', JSON.stringify({
            notification: notification,
            data: data,
        }));

        // Обновить badge если badge_count передан
        if (data?.badge_count) {
            const badgeCount = parseInt(data.badge_count, 10);
            if (!isNaN(badgeCount)) {
                badgeManager.setBadge(badgeCount);
            }
        }

        // Если пришло сообщение чата — триггерить немедленный refresh
        if (data?.type === 'chat_message') {
            this._onChatMessage?.();
        }

        // Push о Payoma-транзакции — рефрешим Total balance на дашборде.
        // Backend перед отправкой push'а уже подтянул свежий баланс из Payoma
        // в v1_2_account_balances, поэтому fetchBalances() покажет новую сумму.
        // Системный баннер при этом продолжает показываться через notifee ниже.
        if (data?.type === 'payoma_transaction') {
            this._onBalanceRefresh?.();
        }

        // Push о confirmed транзакции (inflow / withdraw / interest / etc.)
        // — рефрешим balance tiles и Total balance на dashboard'e. Backend
        // к моменту эмиссии push'а уже применил CreditAmount/DebitAmount
        // через shouldCreditOnConfirm-gate, поэтому fetchBalances() покажет
        // обновлённые значения. 'transaction' — константа
        // PushNotificationDefaultType из backend push-dispatcher'а. Ставится
        // на ВСЕ pushes из v1_2_push_notification_templates, включая
        // pending/opened/closed lifecycle-события — refresh безвреден
        // (idempotent read) и корректно ловит unlock/lock движения баланса.
        if (data?.type === 'transaction') {
            this._onBalanceRefresh?.();
        }

        // chat_sync — silent admin edit/delete signal: refresh the chat
        // and DO NOT show a system banner. Backend sends these whenever
        // an admin edits or soft-deletes a message; the user shouldn't
        // be notified — they'll just see the new content (or absence of
        // a deleted message) on their next view.
        if (data?.type === 'chat_sync') {
            this._onChatMessage?.();
            return;
        }

        // Не показывать уведомление если чат активен (сообщения видны через polling)
        if (this._isChatActive && data?.type === 'chat_message') {
            return;
        }

        // Получить title/body из notification или data (fallback)
        const title = notification?.title || (data as any)?.title || 'New Message';
        const body = notification?.body || (data as any)?.body || '';

        if (title || body) {
            try {
                const channelId = await notifee.createChannel({
                    id: 'default',
                    name: 'Default Notifications',
                    importance: AndroidImportance.HIGH,
                });

                await notifee.displayNotification({
                    title,
                    body,
                    data: data as Record<string, string>,
                    android: {
                        channelId,
                        pressAction: { id: 'default' },
                        smallIcon: 'ic_notification',
                    },
                    ios: {
                        sound: 'default',
                    },
                });
                console.log('[PUSH] Notification displayed via notifee');
            } catch (err) {
                console.error('[PUSH] Failed to display notification via notifee:', err);
            }
        } else {
            console.log('[PUSH] No title/body to display');
        }
    }

    /**
     * Обработка нажатия на уведомление — навигация к соответствующему экрану
     */
    private handleNotificationOpened(remoteMessage: FirebaseMessagingTypes.RemoteMessage): void {
        const data = remoteMessage.data as NotificationData | undefined;

        if (data?.type) {
            switch (data.type) {
                case 'chat_message':
                case 'chat_sync':
                    // chat_sync is normally silent — if the user somehow taps
                    // it (older OS surfacing the empty-body payload), route
                    // to support so they land in the right place.
                    this._onNavigate?.('support');
                    break;
                case 'document':
                    this._onNavigate?.('reports');
                    break;
                case 'balance':
                case 'transaction':
                case 'payoma_transaction':
                    // При открытии push после списания/пополнения карты
                    // форсируем загрузку балансов до навигации. Иначе уже
                    // смонтированный Dashboard может остаться со старым
                    // значением, поскольку foreground-обработчик не срабатывает
                    // для push, который пользователь открыл из системного трея.
                    this._onBalanceRefresh?.();
                    this._onNavigate?.('dashboard');
                    break;
                default:
                    console.log('Unknown notification type:', data.type);
            }
        }
    }

    /**
     * Get the current FCM token (if registered)
     */
    getToken(): string | null {
        return this.currentToken;
    }

    /**
     * Check if notifications are enabled
     */
    async areNotificationsEnabled(): Promise<boolean> {
        try {
            const authStatus = await messaging().hasPermission();
            return (
                authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
                authStatus === messaging.AuthorizationStatus.PROVISIONAL
            );
        } catch (error) {
            console.error('Failed to check notification permission:', error);
            return false;
        }
    }
}

// Export singleton instance
export const notificationService = new NotificationService();
